import { ACTIVITY_TYPE_LABELS, ACTIVITY_TASK_TYPES } from '../../constants/activityTypes.js'
/**
 * panelKit — peças compartilhadas entre LeadPanel e DealPanel.
 *
 * Extraído do LeadPanel para reaproveitar layout, sistema de abas, timeline
 * (Histórico), lista de Atividades, Negócios, Arquivos, Atendimentos, bloco de
 * Notas e os estilos do drawer. Cada painel monta seu próprio "shell" (coluna
 * esquerda com os dados da entidade) e reusa estas peças na coluna direita.
 *
 * Parametrização: componentes que gravam dados recebem callbacks (ex.:
 * NotesSection.onSave, ActivitiesTab.createActivity) para servir tanto a
 * contacts quanto a deals sem duplicar UI.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import ActivityInlineField from './ActivityInlineField.jsx'
import ActivityTypePicker from './ActivityTypePicker.jsx'
import ActivityDeadlineField from './ActivityDeadlineField.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  fmtBRL, getDealItems, createDealItem, deleteDealItem,
  getProducts, getServices, updateActivity, deleteActivity,
  getContracts, createContract, deleteContract,
  getProjects, createProject, updateProject, deleteProject,
  getDealContacts, atListConversations, atListMessages,
  getDealAttachments, signDealAttachmentUpload, createDealAttachment,
  downloadDealAttachment, deleteDealAttachment, pjUploadToSignedUrl,
} from '../../db/api.js'
import { msgTime, groupByDay, relTime, CHANNEL_META } from '../atendimento/atendimentoKit.jsx'
import '../financeiro/contas.css'
import '../atendimento/atendimento.css'

// converte ISO (com tz) → valor de <input type="datetime-local"> ('YYYY-MM-DDTHH:mm')
const toLocalInput = (v) => {
  if (!v) return ''
  const d = new Date(v)
  if (isNaN(d)) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

const EMPTY_ACT = { titulo: '', descricao: '', tipo: 'tarefa', prioridade: 'media', status: 'pendente', prazo: '', responsavel_id: '' }

// ── formatters ────────────────────────────────────────────────────────────
export const fmtDateTime = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d)) return '—'
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
export const fmtDate = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d)) return '—'
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// ── mapas de cor (status, prioridade) ──────────────────────────────────────
export const STATUS_COLORS = {
  prospect: '#6e6e73', lead: '#0071e3', negociacao: '#ff9f0a',
  cliente: '#30d158', recorrente: '#a259ff', churned: '#ff3b30', arquivado: '#9e9e9e',
}
export const DEAL_STATUS_COLORS = { aberto: '#0071e3', ganho: '#30d158', perdido: '#ff3b30' }
const ACT_STATUS_COLORS = { pendente: '#ff9f0a', feito: '#30d158', atrasado: '#ff3b30' }
const PRIO_COLORS = { baixa: '#6e6e73', media: '#0071e3', alta: '#ff3b30' }
const CONTRACT_STATUS_COLORS = {
  ativo: '#30d158', inadimplente: '#ff3b30', cancelado: '#9e9e9e', encerrado: '#6e6e73',
}
const PROJECT_STATUS_COLORS = {
  aguardando_pagamento: '#ff9f0a', ativo: '#0071e3', em_risco: '#ff3b30',
  entregue: '#30d158', em_expansao: '#a259ff',
}
const CONTRACT_STATUS = ['ativo', 'inadimplente', 'cancelado', 'encerrado']
const PROJECT_STATUS = ['aguardando_pagamento', 'ativo', 'em_risco', 'entregue', 'em_expansao']

// Ícone por tipo de evento da timeline (aba Histórico). Cobre os tipos emitidos
// pelo backend (criado/negocio/status/atividade) + os previstos (comentario/
// conversa) p/ quando as integrações chegarem.
export const HIST_ICON = {
  negocio: 'sales',       // Negócios
  criado: 'leads',        // Lead criado / convertido
  lead: 'leads',          // Lead
  atividade: 'check',     // Atividade
  comentario: 'attend',   // Comentário
  conversa: 'whats',      // Conversa (atendimento)
  atendimento: 'whats',
  status: 'status',
  atualizado: 'refresh',
  arquivo: 'page',
  default: 'clock',
}

// Cor de acento por tipo (tinta do ícone + fundo do círculo). 8-díg hex (…1a) = ~10%.
export const HIST_COLOR = {
  negocio: '#30d158',
  criado: '#0071e3',
  lead: '#0071e3',
  atividade: '#a259ff',
  comentario: '#ff9f0a',
  conversa: '#25d366',
  atendimento: '#25d366',
  status: '#0071e3',
  atualizado: '#6e6e73',
  arquivo: '#6e6e73',
  default: '#6e6e73',
}

// ── Notas (visualizar / editar / salvar) ────────────────────────────────
// `onSave(text)` persiste; `resetKey` re-sincroniza ao trocar de entidade.
function NotesSection({ title = 'Notas', initial, onSave, resetKey, footerLeft }) {
  const [value, setValue] = useState(initial || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => { setValue(initial || ''); setSaved(false) }, [initial, resetKey])

  const dirty = (value || '') !== (initial || '')

  const save = async () => {
    if (saving || !dirty) return
    setSaving(true); setErr(null); setSaved(false)
    try {
      await onSave(value || null)
      setSaved(true)
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  return (
    <div style={S.section}>
      <div style={S.notesHead}>
        <div style={S.sectionTitle}>{title}</div>
        {saved && !dirty && <span style={S.savedTag}>salvo ✓</span>}
      </div>
      {err && <div style={S.error}>{err}</div>}
      <textarea
        style={S.notesArea}
        value={value}
        placeholder="Anotações…"
        onChange={(e) => { setValue(e.target.value); setSaved(false) }}
      />
      <div style={{ ...S.notesFooter, justifyContent: footerLeft ? 'space-between' : 'flex-end' }}>
        {footerLeft || null}
        <button style={{ ...S.btnPrimarySm, opacity: dirty ? 1 : 0.5 }} onClick={save} disabled={!dirty || saving}>
          {saving ? 'Salvando…' : 'Salvar notas'}
        </button>
      </div>
    </div>
  )
}

// ── Aba Atividades ──────────────────────────────────────────────────────
// `createActivity(payload)` grava (contact ou deal); `onCreated()` recarrega.
// Editar/concluir usam updateActivity (id global) — servem lead E deal sem props
// extras. 'atrasado' vem derivado do backend (prazo vencido + status pendente).
export function ActivitiesTab({ items, loading, members, createActivity, onCreated, dealDesign = false }) {
  const { user } = useApp()
  const [mode, setMode] = useState('none')     // none | add | edit
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_ACT)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState(null)   // item em "concluir"
  const [celebratingId, setCelebratingId] = useState(null)
  const [celebrationWasLate, setCelebrationWasLate] = useState(false)
  const [err, setErr] = useState(null)

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const openAdd = () => {
    const defaultDeadline = new Date()
    setErr(null); setEditingId(null); setForm({ ...EMPTY_ACT, tipo: dealDesign ? '' : EMPTY_ACT.tipo, prazo: dealDesign ? toLocalInput(defaultDeadline) : EMPTY_ACT.prazo, responsavel_id: dealDesign ? user?.id || '' : '' })
    setMode((m) => (m === 'add' ? 'none' : 'add'))
  }
  const openEdit = (a) => {
    setErr(null); setEditingId(a.id); setMode('edit')
    setForm({
      titulo: a.titulo || '', descricao: a.descricao || '', tipo: a.tipo || 'tarefa',
      prioridade: a.prioridade || 'media',
      status: a.status === 'atrasado' ? 'pendente' : (a.status || 'pendente'),
      prazo: toLocalInput(a.prazo), responsavel_id: a.responsavel_id || '',
    })
  }
  const closeForm = () => { setMode('none'); setEditingId(null); setForm(EMPTY_ACT) }

  const submit = async () => {
    if (!form.titulo.trim() || (dealDesign && !form.tipo) || saving) return
    setSaving(true); setErr(null)
    try {
      // O calendário usa hora local; a API precisa receber um instante com fuso explícito.
      const payload = {
        titulo: form.titulo.trim(), descricao: form.descricao.trim() || null, tipo: form.tipo, prioridade: form.prioridade,
        status: form.status, prazo: form.prazo ? new Date(form.prazo).toISOString() : null, responsavel_id: form.responsavel_id || null,
      }
      if (mode === 'edit') await updateActivity(editingId, payload)
      else await createActivity(payload)
      closeForm()
      await onCreated()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const concluir = async (a) => {
    setBusyId(a.id); setErr(null)
    try {
      await updateActivity(a.id, { status: 'feito' })
      await onCreated()
      if (dealDesign) { setCelebrationWasLate(a.status === 'atrasado'); setCelebratingId(a.id) }
    }
    catch (e) { setErr(e.message) } finally { setBusyId(null) }
  }

  const excluir = async (a) => {
    if (!window.confirm(`Excluir "${a.titulo}"?`)) return
    setBusyId(a.id); setErr(null)
    try {
      await deleteActivity(a.id)
      if (editingId === a.id) closeForm()
      await onCreated()
    } catch (e) { setErr(e.message) } finally { setBusyId(null) }
  }

  const showForm = mode === 'add' || mode === 'edit'
  const typeLabels = ACTIVITY_TYPE_LABELS
  const selectableTypes = dealDesign
    ? [...ACTIVITY_TASK_TYPES, ...(form.tipo === 'mensagem' ? ['mensagem'] : [])]
    : ['tarefa', 'evento', 'follow_up', 'mensagem', 'pagamento']
  const statusLabels = { pendente: 'Pendente', feito: 'Concluída', atrasado: 'Atrasada', cancelado: 'Cancelada' }

  return (
    <div className={dealDesign ? 'dp-activities' : undefined}>
      <div style={S.tabActions} className={dealDesign ? 'dp-activities-toolbar' : undefined}>
        {dealDesign && !loading && <span className="dp-activities-summary">{items.filter(item => item.status !== 'feito').length} pendentes · {items.filter(item => item.status === 'feito').length} concluídas</span>}
        <button style={S.btnPrimarySm} onClick={openAdd}>
          <Icon n="plus" s={13} sw={2} /> Nova atividade
        </button>
      </div>

      {showForm && (
        <div style={S.actForm} className={dealDesign ? 'dp-activity-form' : undefined}>
          <div style={S.actFormHead}>
            <span style={S.actFormTitle}>
              <Icon n={mode === 'edit' ? 'settings' : 'plus'} s={14} sw={2} />
              {mode === 'edit' ? 'Editar atividade' : 'Nova atividade'}
            </span>
            <button style={S.actFormClose} onClick={closeForm} title="Fechar">
              <Icon n="x" s={16} sw={2} />
            </button>
          </div>
          {err && <div style={S.error}>{err}</div>}

{!dealDesign && <>
          <div style={S.actField}>
            <label style={S.miniLabel}>Título</label>
            <input style={S.input} placeholder="Ex.: Ligar para o cliente" value={form.titulo} autoFocus onChange={(e) => set('titulo', e.target.value)} />
          </div>
          </>}

          <div style={S.actField}>
            <label style={S.miniLabel}>Tipo</label>
            {dealDesign ? <ActivityTypePicker value={form.tipo} placeholder="Selecionar um tipo de tarefa…" types={selectableTypes} onChange={type => setForm(current => ({ ...current, tipo: type, titulo: current.titulo.trim() ? current.titulo : typeLabels[type] || type }))} /> : <div style={S.chipRow}>
              {selectableTypes.map((t) => (
                <button
                  key={t}
                  type="button"
                  style={{ ...S.choiceChip, ...(form.tipo === t ? S.choiceChipActive : null) }}
                  onClick={() => set('tipo', t)}
                >
                  {dealDesign ? typeLabels[t] : t.replace('_', ' ')}
                </button>
              ))}
            </div>}
          </div>

          {dealDesign && <>
          <div style={S.actField}>
            <label style={S.miniLabel}>Título</label>
            <input style={S.input} placeholder="Ex.: Ligar para o cliente" value={form.titulo} autoFocus onChange={(e) => set('titulo', e.target.value)} />
          </div>
          </>}

          <div style={S.actField}>
            <label style={S.miniLabel}>Descrição</label>
            <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Detalhes da atividade (opcional)" value={form.descricao} onChange={(e) => set('descricao', e.target.value)} />
          </div>

          <div style={S.addGrid} className={dealDesign ? 'dp-activity-form-grid' : undefined}>
            <div style={S.actField}>
              <label style={S.miniLabel}>Prioridade</label>
              {dealDesign ? <ActivityTypePicker value={form.prioridade} types={['baixa', 'media', 'alta']} labels={{ baixa: 'Baixa', media: 'Média', alta: 'Alta' }} label="Prioridade" searchable={false} onChange={value => set('prioridade', value)} /> : <select style={S.input} value={form.prioridade} onChange={(e) => set('prioridade', e.target.value)}>
                {['baixa', 'media', 'alta'].map((p) => <option key={p} value={p}>{dealDesign ? ({ baixa: 'Baixa', media: 'Média', alta: 'Alta' })[p] : p}</option>)}
              </select>}
            </div>
            <div style={S.actField}>
              <label style={S.miniLabel}>Status</label>
              {dealDesign ? <ActivityTypePicker value={form.status} types={['pendente', 'feito']} labels={statusLabels} label="Status" searchable={false} onChange={value => set('status', value)} /> : <select style={S.input} value={form.status} onChange={(e) => set('status', e.target.value)}>
                {['pendente', 'feito'].map((s) => <option key={s} value={s}>{dealDesign ? statusLabels[s] : s}</option>)}
              </select>}
            </div>
            <div style={S.actField}>
              <label style={S.miniLabel}>Prazo</label>
              {dealDesign ? <ActivityDeadlineField value={form.prazo} onChange={value => set('prazo', value)} /> : <input style={S.input} type="datetime-local" value={form.prazo} onChange={(e) => set('prazo', e.target.value)} />}
            </div>
            <div style={S.actField}>
              <label style={S.miniLabel}>Responsável</label>
              {dealDesign ? <ActivityTypePicker value={form.responsavel_id} types={['', ...new Set([...(user?.id ? [user.id] : []), ...members.map(member => member.id), ...(form.responsavel_id ? [form.responsavel_id] : [])])]} labels={{ '': 'Sem responsável', ...(form.responsavel_id ? { [form.responsavel_id]: 'Responsável atual' } : {}), ...(user?.id ? { [user.id]: user.name || 'Você' } : {}), ...Object.fromEntries(members.map(member => [member.id, member.nome])) }} label="Responsável" searchable={true} onChange={value => set('responsavel_id', value)} /> : <select style={S.input} value={form.responsavel_id} onChange={(e) => set('responsavel_id', e.target.value)}>
                <option value="">Sem responsável</option>
                {dealDesign && user?.id && !members.some(m => m.id === user.id) && <option value={user.id}>{user.name || 'Você'}</option>}
                {members.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>}
            </div>
          </div>

          <div style={S.actFormFooter}>
            <button style={S.btnGhostSm} onClick={closeForm}>Cancelar</button>
            <button style={S.btnPrimarySm} onClick={submit} disabled={saving || !form.titulo.trim() || (dealDesign && !form.tipo)}>
              {saving ? 'Salvando…' : mode === 'edit' ? 'Salvar alterações' : 'Adicionar atividade'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={S.muted}>Carregando…</div>
      ) : !items.length ? (
        <div style={S.empty}>Nenhuma atividade vinculada.</div>
      ) : (
        <div>
          {items.map((a) => {
            const done = a.status === 'feito'
            if (dealDesign) return <article key={a.id} className={`dp-activity-card ${done ? 'is-done' : a.status === 'atrasado' ? 'is-late' : ''} ${celebratingId === a.id ? 'is-celebrating' : ''}`} onAnimationEnd={event => { if (event.target === event.currentTarget) setCelebratingId(current => current === a.id ? null : current) }}>
              {celebratingId === a.id && <div className="dp-task-finish-scene" style={{ '--finish-start-bg': celebrationWasLate ? '#fee4e2' : '#fff5d5', '--finish-start-color': celebrationWasLate ? '#b42318' : '#a16b00' }} aria-hidden="true">
                <span className="dp-task-finish-sweep" />
                <span className="dp-task-finish-strike" />
                <span className="dp-task-finish-symbol"><span className="dp-task-finish-before"><Icon n="calendar" s={23} /></span><svg className="dp-task-finish-after" viewBox="0 0 24 24" width="27" height="27" fill="none"><path d="m5 12 4 4L19 6" pathLength="1" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                <span className="dp-task-finish-caption">Tarefa concluída</span>
              </div>}
              <span className="dp-activity-icon" aria-hidden="true"><Icon n={done ? 'check' : 'calendar'} s={23} /></span>
              <div className="dp-activity-main">
                <div className="dp-activity-meta"><span>{typeLabels[a.tipo] || a.tipo || 'Atividade'}</span><span className="dp-activity-status">{statusLabels[a.status] || a.status}</span><span className={`dp-activity-priority priority-${a.prioridade}`}>Prioridade {({ media: 'média', alta: 'alta', baixa: 'baixa' })[a.prioridade] || a.prioridade || 'não definida'}</span></div>
                <h4>{a.titulo}</h4>
                {a.descricao && <p className="dp-activity-description" style={{ margin: '4px 0 0', fontSize: 13, color: '#475467', whiteSpace: 'pre-wrap' }}>{a.descricao}</p>}
                <div className="dp-activity-context">
                  <ActivityInlineField activity={a} field="responsavel_id" members={members} label={a.responsavel_nome || 'Sem responsável'} disabled={busyId === a.id || saving} onSaved={onCreated} />
                  <ActivityInlineField activity={a} field="prazo" members={members} label={a.prazo ? `Prevista para ${['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'][new Date(a.prazo).getDay()]}, ${fmtDateTime(a.prazo)}` : 'Sem prazo definido'} disabled={busyId === a.id || saving} onSaved={onCreated} />
                </div>
              </div>
              <div className="dp-activity-actions">
                {done ? <span className="dp-activity-completed-badge" role="status"><svg viewBox="0 0 20 20" width="17" height="17" fill="none" aria-hidden="true"><path d="m4 10 4 4 8-8" pathLength="1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>Concluída</span> : <button type="button" className="dp-activity-complete" onClick={() => concluir(a)} disabled={busyId === a.id}>{busyId === a.id ? 'Salvando…' : 'Concluir'}</button>}
                <div className="dp-activity-corner-actions"><button type="button" onClick={() => openEdit(a)} disabled={busyId === a.id}>Editar</button>
                <button type="button" className="dp-activity-delete" aria-label={`Excluir atividade ${a.titulo}`} title="Excluir atividade" onClick={() => excluir(a)} disabled={busyId === a.id}><Icon n="trash" s={12} /></button></div>
              </div>
            </article>
            return (
              <div key={a.id} style={S.listRow}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...S.listTitle, ...(done ? S.doneTitle : null) }}>{a.titulo}</div>
                  {a.descricao && <div style={{ ...S.listMeta, whiteSpace: 'pre-wrap' }}>{a.descricao}</div>}
                  <div style={S.listMeta}>
                    {a.responsavel_nome ? `${a.responsavel_nome} · ` : ''}
                    {a.prazo ? fmtDateTime(a.prazo) : 'sem prazo'}
                  </div>
                </div>
                <div style={S.rowPills}>
                  <span style={{ ...S.pill, color: PRIO_COLORS[a.prioridade] || 'var(--sec)', borderColor: PRIO_COLORS[a.prioridade] || 'var(--soft)' }}>{a.prioridade}</span>
                  <span style={{ ...S.pill, color: ACT_STATUS_COLORS[a.status] || 'var(--sec)', borderColor: ACT_STATUS_COLORS[a.status] || 'var(--soft)' }}>{a.status}</span>
                  {!done && (
                    <button style={S.actBtn} title="Concluir" onClick={() => concluir(a)} disabled={busyId === a.id}>
                      <Icon n="check" s={14} c="#30d158" />
                    </button>
                  )}
                  <button style={S.actBtn} title="Editar" onClick={() => openEdit(a)}>
                    <Icon n="settings" s={13} />
                  </button>
                  <button style={S.actBtn} title="Excluir" onClick={() => excluir(a)} disabled={busyId === a.id}>
                    <Icon n="trash" s={13} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {dealDesign && !showForm && err && <div role="alert" style={S.error}>{err}</div>}
    </div>
  )
}

// ── Aba Negócios ────────────────────────────────────────────────────────
// Lista de negócios como CARDS (padrão Datacrazy). `onOpen(id)` torna cada card
// clicável (abre a tela "Informações do Negócio"); sem ele, os cards são só
// leitura. `onCreate` mostra o botão de criar. Card = resumo (nome, valor,
// status, pipeline/fase, data). Detalhes completos ficam na tela de info.
export function DealsTab({ items, loading, onCreate, onOpen, onRemove, emptyMsg = 'Nenhum negócio vinculado.', createLabel = 'Criar negócio', removeTitle = 'Remover negócio', hideRemoveId = null }) {
  return (
    <div>
      {onCreate && (
        <div style={S.tabActions}>
          <button style={S.btnPrimarySm} onClick={onCreate}>
            <Icon n="plus" s={13} sw={2} /> {createLabel}
          </button>
        </div>
      )}
      {loading ? (
        <div style={S.muted}>Carregando…</div>
      ) : !items.length ? (
        <div style={S.empty}>{emptyMsg}</div>
      ) : (
        <div style={S.dealCards}>
          {items.map((d) => {
            const color = DEAL_STATUS_COLORS[d.status] || 'var(--sec)'
            const clickable = !!onOpen
            return (
              <div
                key={d.id}
                style={{ ...S.dealCard, ...(clickable ? S.dealCardClickable : null) }}
                onClick={clickable ? () => onOpen(d.id) : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onOpen(d.id) } : undefined}
                title={clickable ? 'Ver informações do negócio' : undefined}
              >
                <div style={S.dealCardMain}>
                  <div style={S.dealCardName}>
                    {d.titulo || 'Negócio'}
                    {d.is_primary && <span style={S.primaryTag}>principal</span>}
                  </div>
                  <div style={S.dealCardMeta}>
                    <span style={S.dealCardMetaItem}>
                      <Icon n="chart" s={12} /> {d.pipeline_nome || '—'}{d.stage_nome ? ` · ${d.stage_nome}` : ''}
                    </span>
                    {d.valor != null && (
                      <span style={S.dealCardMetaItem}><Icon n="currency" s={12} /> {fmtBRL(d.valor)}</span>
                    )}
                    {d.created_at && (
                      <span style={S.dealCardMetaItem}><Icon n="calendar" s={12} /> {fmtDate(d.created_at)}</span>
                    )}
                  </div>
                </div>
                <div style={S.dealCardSide}>
                  <span style={{ ...S.pill, color, borderColor: color }}>{d.status}</span>
                  {onRemove && d.id !== hideRemoveId && (
                    <button
                      style={S.dealRemove}
                      onClick={(e) => { e.stopPropagation(); onRemove(d) }}
                      title={removeTitle}
                    >
                      <Icon n="trash" s={15} sw={2} />
                    </button>
                  )}
                </div>
                {clickable && <div style={S.dealCardChevron}><Icon n="chevron_r" s={16} /></div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Diálogo de confirmação (substitui window.confirm) ───────────────────────
// Modal inline no padrão do drawer. `open` controla visibilidade; `onConfirm`
// dispara a ação; `onCancel` fecha. `danger` pinta o botão de ação de vermelho.
export function ConfirmDialog({
  open, title = 'Confirmar', message, confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar', danger = false, busy = false, onConfirm, onCancel,
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null
  return (
    <div style={S.mOverlay} onMouseDown={onCancel}>
      <div style={S.mBox} onMouseDown={(e) => e.stopPropagation()}>
        <div style={S.mHead}>
          <h4 style={S.mTitle}>{title}</h4>
          <button style={S.mX} onClick={onCancel}>×</button>
        </div>
        {message && <div style={S.confirmMsg}>{message}</div>}
        <div style={S.addFooter}>
          <button style={S.btnGhostSm} onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button
            style={danger ? S.btnDangerSm : S.btnPrimarySm}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? 'Aguarde…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Aba Arquivos ────────────────────────────────────────────────────────────
// Com `dealId`: upload/download/exclusão dos arquivos do negócio (Supabase
// Storage, mediado pelo backend). Sem `dealId` (LeadView/ClienteView) segue como
// scaffold, sem backend ainda.
const MAX_FILE_MB = 25

const fmtFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function FilesTab({ dealId = null }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(!!dealId)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)
  const [toDelete, setToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const load = async () => {
    try { setItems(await getDealAttachments(dealId)); setErr(null) }
    catch (e) { setErr(e.message) }
    finally { setLoading(false) }
  }
  useEffect(() => {
    if (!dealId) return
    setLoading(true); setItems([]); setErr(null)
    load()
  }, [dealId])

  const onPick = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''   // permite re-selecionar o mesmo arquivo
    if (!files.length) return
    const tooBig = files.find((f) => f.size > MAX_FILE_MB * 1024 * 1024)
    if (tooBig) { setErr(`"${tooBig.name}" passa do limite de ${MAX_FILE_MB} MB.`); return }
    setUploading(true); setErr(null)
    try {
      for (const file of files) {
        const { upload_url, storage_path } = await signDealAttachmentUpload(dealId, file.name)
        await pjUploadToSignedUrl(upload_url, file)
        await createDealAttachment(dealId, {
          nome: file.name, storage_path,
          mime: file.type || null, tamanho: file.size || null,
        })
      }
    } catch (e2) { setErr(e2.message) } finally {
      setUploading(false)
      await load()
    }
  }

  const download = async (a) => {
    try {
      const { url } = await downloadDealAttachment(a.id)
      window.open(url, '_blank', 'noopener')
    } catch (e2) { setErr(e2.message) }
  }

  const confirmDelete = async () => {
    setDeleting(true)
    try { await deleteDealAttachment(toDelete.id); setToDelete(null); await load() }
    catch (e2) { setErr(e2.message); setToDelete(null) }
    finally { setDeleting(false) }
  }

  if (!dealId) {
    return (
      <div>
        <div style={S.tabActions}>
          <button style={S.btnGhostSm} disabled title="Disponível em breve">
            <Icon n="upload" s={13} /> Enviar arquivo
          </button>
        </div>
        <div style={S.empty}>
          Nenhum arquivo enviado.
          <div style={S.emptyHint}>Os arquivos ficam disponíveis no painel do negócio.</div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={S.tabActions}>
        <label style={{ ...S.btnGhostSm, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
          <Icon n="upload" s={13} /> {uploading ? 'Enviando…' : 'Enviar arquivo'}
          <input type="file" multiple hidden onChange={onPick} disabled={uploading} />
        </label>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {loading ? (
        <div style={S.muted}>Carregando…</div>
      ) : !items.length ? (
        <div style={S.empty}>
          Nenhum arquivo enviado.
          <div style={S.emptyHint}>Limite de {MAX_FILE_MB} MB por arquivo.</div>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          {items.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--soft)' }}>
              <button
                type="button" onClick={() => download(a)} title="Baixar"
                style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: 'var(--text)' }}
              >
                <Icon n="page" s={15} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nome}</div>
                  <div style={{ fontSize: 12, color: 'var(--sec)' }}>
                    {[fmtFileSize(a.tamanho), fmtDateTime(a.created_at)].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </button>
              <button type="button" style={S.btnGhostSm} onClick={() => setToDelete(a)} title="Excluir">
                <Icon n="trash" s={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog
        open={!!toDelete} title="Excluir arquivo" danger busy={deleting}
        message={toDelete ? `Excluir o arquivo "${toDelete.nome}"? Essa ação não pode ser desfeita.` : ''}
        confirmLabel="Excluir"
        onConfirm={confirmDelete} onCancel={() => setToDelete(null)}
      />
    </div>
  )
}

// ── Aba Projetos (módulo Projetos / migration 022) ──────────────────────────
// Lista os projetos de entrega vinculados ao cliente (project.contact_id /
// company_id). Cada card mostra status, prioridade, progresso (tarefas
// concluídas/total) e a origem (deal). Clicar abre o Kanban do projeto.
// Status do módulo 022 (saúde macro do projeto) — distinto do mapa legado da 016.
const PROJETO_022_STATUS_COLORS = {
  ativo: '#30d158', pausado: '#ff9f0a', concluido: 'var(--sec)', arquivado: '#8e8e93',
}

export function ProjetosTab({ items, loading, onOpen, emptyMsg = 'Este cliente ainda não possui projetos.' }) {
  return (
    <div>
      {loading ? (
        <div style={S.muted}>Carregando…</div>
      ) : !items.length ? (
        <div style={S.empty}>{emptyMsg}</div>
      ) : (
        <div style={S.dealCards}>
          {items.map((p) => {
            const color = PROJETO_022_STATUS_COLORS[p.status] || 'var(--sec)'
            const total = Number(p.tasks_total || 0)
            const done = Number(p.tasks_done || 0)
            const pct = total > 0 ? Math.round((done / total) * 100) : 0
            return (
              <div
                key={p.id}
                style={{ ...S.dealCard, ...S.dealCardClickable }}
                onClick={() => onOpen(p.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p.id) }}
                title="Abrir projeto"
              >
                <div style={S.dealCardHead}>
                  <div style={S.dealCardName}>{p.nome || 'Projeto'}</div>
                  <span style={{ ...S.pill, color, borderColor: color }}>{p.status}</span>
                </div>
                <div style={S.dealCardMeta}>
                  <span style={S.dealCardMetaItem}><Icon n="check" s={12} /> {done}/{total} tarefas</span>
                  {p.prioridade && (
                    <span style={S.dealCardMetaItem}><Icon n="target" s={12} /> {p.prioridade}</span>
                  )}
                  {p.prazo && (
                    <span style={S.dealCardMetaItem}><Icon n="calendar" s={12} /> {fmtDate(p.prazo)}</span>
                  )}
                  {p.deal_titulo && (
                    <span style={S.dealCardMetaItem}><Icon n="sales" s={12} /> {p.deal_titulo}</span>
                  )}
                </div>
                <div style={{ height: 4, borderRadius: 2, background: 'rgba(128,128,128,.18)', marginTop: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width .3s' }} />
                </div>
                <div style={S.dealCardChevron}><Icon n="chevron_r" s={16} /></div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Aba Itens do negócio (deal_items — Fase 2 / migration 016) ──────────────
// Adicionar item sempre parte do Catálogo (produtos/serviços já cadastrados).
// Cadastrar um produto/serviço novo é feito em Comercial → Produtos/Serviços —
// não mais direto por aqui (evita duplicar item no catálogo por engano).
// quantidade × preço = total (o backend calcula valor_total, GENERATED).
// `onTotalChange(total)` avisa o container (ex.: DealModal usa p/ o valor).
//
// Serviço ≠ produto: em vez de "quantidade" (não faz sentido pra recorrência),
// o usuário escolhe a periodicidade de cobrança daquele serviço NESTE negócio
// (pode divergir do padrão do catálogo — migration 032/deal_items.periodicidade).
// A quantidade do item fica implícita = 1.
const ITEM_PERIODICIDADES = ['mensal', 'trimestral', 'semestral', 'anual', 'unico']
const PERIODICIDADE_LABELS = {
  mensal: 'Mensal', trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual', unico: 'Único',
}
const EMPTY_ITEM = { ref: '', quantidade: '1', preco_unitario: '', periodicidade: 'mensal' }

// Modal de adicionar item — antes o formulário expandia dentro da página e a
// deixava longa/poluída; agora abre num modal. `onSubmit(form)` cria o vínculo
// do item do catálogo (o container faz o create/reload); joga erro p/ o modal
// exibir e ficar aberto. `products`/`services` vêm do container.
function ItemFormModal({ products, services, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_ITEM)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const isServico = form.ref.startsWith('servico:')

  const catalogOpts = useMemo(() => ([
    ...products.map((p) => ({ value: `produto:${p.id}`, label: `${p.nome}`, preco: p.preco })),
    ...services.map((s) => ({ value: `servico:${s.id}`, label: `${s.nome} (serviço)`, preco: s.preco, periodicidade: s.periodicidade })),
  ]), [products, services])

  const onPickRef = (value) => {
    const opt = catalogOpts.find((o) => o.value === value)
    const servico = value.startsWith('servico:')
    setForm((f) => ({
      ...f,
      ref: value,
      preco_unitario: opt?.preco != null ? String(opt.preco) : f.preco_unitario,
      quantidade: servico ? '1' : f.quantidade,
      periodicidade: servico ? (opt?.periodicidade || f.periodicidade || 'mensal') : f.periodicidade,
    }))
  }

  const canSubmit = !!form.ref

  const previewTotal = (Number(form.quantidade) || 0) *
    (form.preco_unitario === '' ? 0 : Number(form.preco_unitario) || 0)

  const submit = async () => {
    if (!canSubmit || saving) return
    setSaving(true); setErr(null)
    try { await onSubmit(form); onClose() }
    catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: 480 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Icon n="sales" s={16} /> Adicionar item
          </span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>

        <div className="fc-modal-body">
          {err && <div className="fc-error">{err}</div>}

          {catalogOpts.length ? (
            <div className="fc-field">
              <label className="fc-label">Produto / serviço cadastrado</label>
              <select className="fc-select" value={form.ref} onChange={(e) => onPickRef(e.target.value)} autoFocus>
                <option value="">— escolha um produto/serviço —</option>
                {catalogOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          ) : (
            <div className="fc-field" style={{ fontSize: 12, color: '#98a2b3' }}>
              Nenhum produto/serviço cadastrado. Cadastre em Comercial → Produtos ou Comercial → Serviços para poder adicioná-lo aqui.
            </div>
          )}

          <div className="fc-grid-2">
            {isServico ? (
              <div className="fc-field">
                <label className="fc-label">Recorrência</label>
                <select className="fc-select" value={form.periodicidade}
                  onChange={(e) => set('periodicidade', e.target.value)}>
                  {ITEM_PERIODICIDADES.map((p) => <option key={p} value={p}>{PERIODICIDADE_LABELS[p]}</option>)}
                </select>
              </div>
            ) : (
              <div className="fc-field">
                <label className="fc-label">Quantidade</label>
                <input className="fc-input" type="number" min="0" value={form.quantidade}
                  onChange={(e) => set('quantidade', e.target.value)} />
              </div>
            )}
            <div className="fc-field">
              <label className="fc-label">Preço unitário (R$)</label>
              <input className="fc-input" type="number" min="0" value={form.preco_unitario}
                onChange={(e) => set('preco_unitario', e.target.value)} placeholder="0,00" />
            </div>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '11px 14px', borderRadius: 10, border: '1px solid #eaecf0', background: '#f9fafb',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: '#98a2b3' }}>
              Subtotal do item
            </span>
            <span style={{ fontSize: 18, fontWeight: 700, color: '#101828' }}>{fmtBRL(previewTotal)}</span>
          </div>
        </div>

        <div className="fc-modal-foot">
          <button className="fc-ctrl" onClick={onClose}>Cancelar</button>
          <button className="fc-ctrl primary" onClick={submit} disabled={saving || !canSubmit}>
            {saving ? 'Salvando…' : 'Adicionar item'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function ItemsTab({ dealId, onTotalChange }) {
  const [items, setItems] = useState([])
  const [products, setProducts] = useState([])
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [err, setErr] = useState(null)

  const total = useMemo(() => items.reduce((s, i) => s + (Number(i.total_com_desconto ?? i.valor_total) || 0), 0), [items])
  useEffect(() => { onTotalChange?.(total) }, [total, onTotalChange])

  const reload = async () => setItems(await getDealItems(dealId))

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const [its, ps, ss] = await Promise.all([getDealItems(dealId), getProducts(), getServices()])
        if (!alive) return
        setItems(its); setProducts(ps); setServices(ss)
      } catch (e) { if (alive) setErr(e.message) } finally { if (alive) setLoading(false) }
    })()
    return () => { alive = false }
  }, [dealId])

  // vincula um item já cadastrado no catálogo ao negócio. Chamado pelo
  // ItemFormModal; joga erro p/ o modal tratar.
  const submitItem = async (form) => {
    const preco = form.preco_unitario === '' ? 0 : Number(form.preco_unitario)
    const [tipo, id] = form.ref.split(':')
    const isServico = tipo === 'servico'
    const opt = [...products, ...services].find((x) => x.id === id)
    await createDealItem(dealId, {
      productId: isServico ? null : id,
      serviceId: isServico ? id : null,
      descricao: opt?.nome || null,
      quantidade: isServico ? 1 : (Number(form.quantidade) || 1),
      precoUnitario: preco,
      periodicidade: isServico ? form.periodicidade : null,
    })
    await reload()
  }

  const remove = async (itemId) => {
    try { await deleteDealItem(dealId, itemId); await reload() }
    catch (e) { setErr(e.message) }
  }

  return (
    <div>
      <div style={S.tabActions}>
        <button style={S.btnPrimarySm} onClick={() => setShowForm(true)}>
          <Icon n="plus" s={13} sw={2} /> Adicionar item
        </button>
      </div>
      {err && <div style={S.error}>{err}</div>}

      {loading ? (
        <div style={S.muted}>Carregando…</div>
      ) : !items.length ? (
        <div style={S.empty}>Nenhum item adicionado.</div>
      ) : (
        <div>
          {items.map((i) => (
            <div key={i.id} style={S.listRow}>
              <div style={{ minWidth: 0 }}>
                <div style={S.listTitle}>
                  {i.catalogo_nome || i.descricao || 'Item'}
                  <span style={S.primaryTag}>{i.tipo === 'servico' ? 'serviço' : 'produto'}</span>
                </div>
                <div style={S.listMeta}>
                  {i.tipo === 'servico'
                    ? `${PERIODICIDADE_LABELS[i.periodicidade] || 'Recorrente'} · ${fmtBRL(i.preco_unitario)}`
                    : `${(+i.quantidade)} × ${fmtBRL(i.preco_unitario)}`}
                </div>
              </div>
              <div style={S.rowPills}>
                <span style={S.itemTotal}>{fmtBRL(i.total_com_desconto ?? i.valor_total)}</span>
                <button style={S.itemRemove} onClick={() => remove(i.id)} title="Remover item">
                  <Icon n="x" s={13} sw={2} />
                </button>
              </div>
            </div>
          ))}
          <div style={S.itemsTotalRow}>
            <span>Total dos itens</span>
            <span style={S.itemsTotalValue}>{fmtBRL(total)}</span>
          </div>
        </div>
      )}

      {showForm && (
        <ItemFormModal
          products={products} services={services}
          onClose={() => setShowForm(false)} onSubmit={submitItem}
        />
      )}
    </div>
  )
}

// ── Aba Contrato (contracts + projetos — F3 / migration 016) ────────────────
// Auto-gerencia o CRUD via /api/crm. Um deal ganho recorrente gera um contrato
// (valor total, MRR, vigência, status); cada contrato tem projetos (entregas da
// Operação). Prefill do valor a partir do deal. Excluir o contrato apaga os
// projetos (cascade no backend). `deal` é usado só p/ pré-preencher o valor.
export function ContractsTab({ dealId, deal }) {
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const emptyForm = { valor_total: '', mrr: '', data_inicio: '', data_fim: '', status: 'ativo' }
  const [form, setForm] = useState(emptyForm)

  const reload = async () => setContracts(await getContracts(dealId))

  useEffect(() => {
    let alive = true
    setLoading(true)
    getContracts(dealId)
      .then((cs) => alive && setContracts(cs))
      .catch((e) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [dealId])

  const openAdd = () => {
    setErr(null)
    setForm({ ...emptyForm, valor_total: deal?.valor != null ? String(deal.valor) : '' })
    setAdding((v) => !v)
  }

  const submit = async () => {
    if (saving) return
    setSaving(true); setErr(null)
    try {
      await createContract({
        deal_id: dealId,
        valor_total: form.valor_total === '' ? null : Number(form.valor_total),
        mrr: form.mrr === '' ? null : Number(form.mrr),
        data_inicio: form.data_inicio || null,
        data_fim: form.data_fim || null,
        status: form.status,
      })
      setForm(emptyForm); setAdding(false)
      await reload()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const remove = async (id) => {
    if (!window.confirm('Excluir este contrato? Os projetos vinculados também serão removidos.')) return
    try { await deleteContract(id); await reload() }
    catch (e) { setErr(e.message) }
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  // Regra de negócio: contrato só existe para um negócio GANHO (recorrente).
  // Antes disso não há o que gerar — some o botão e mostra um hint discreto.
  const canCreate = deal?.status === 'ganho'

  // Sem contrato e negócio ainda não ganho: nada a exibir além do hint.
  if (!loading && !contracts.length && !canCreate) {
    return (
      <div style={S.contractLocked}>
        <Icon n="suggest" s={14} />
        <span>O contrato fica disponível quando o negócio for marcado como <b>ganho</b>.</span>
      </div>
    )
  }

  return (
    <div>
      {canCreate && (
        <div style={S.tabActions}>
          <button style={S.btnPrimarySm} onClick={openAdd}>
            <Icon n="plus" s={13} sw={2} /> Gerar contrato
          </button>
        </div>
      )}

      {adding && (
        <div style={S.addBox}>
          {err && <div style={S.error}>{err}</div>}
          <div style={S.addGrid}>
            <div>
              <label style={S.miniLabel}>Valor total (R$)</label>
              <input style={S.input} type="number" min="0" value={form.valor_total}
                onChange={(e) => set('valor_total', e.target.value)} placeholder="0,00" />
            </div>
            <div>
              <label style={S.miniLabel}>MRR (R$/mês)</label>
              <input style={S.input} type="number" min="0" value={form.mrr}
                onChange={(e) => set('mrr', e.target.value)} placeholder="0,00" />
            </div>
            <div>
              <label style={S.miniLabel}>Início</label>
              <input style={S.input} type="date" value={form.data_inicio}
                onChange={(e) => set('data_inicio', e.target.value)} />
            </div>
            <div>
              <label style={S.miniLabel}>Fim</label>
              <input style={S.input} type="date" value={form.data_fim}
                onChange={(e) => set('data_fim', e.target.value)} />
            </div>
          </div>
          <select style={S.input} value={form.status} onChange={(e) => set('status', e.target.value)}>
            {CONTRACT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={S.addFooter}>
            <button style={S.btnGhostSm} onClick={() => setAdding(false)}>Cancelar</button>
            <button style={S.btnPrimarySm} onClick={submit} disabled={saving}>
              {saving ? 'Salvando…' : 'Gerar contrato'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={S.muted}>Carregando…</div>
      ) : !contracts.length ? (
        <div style={S.empty}>
          Nenhum contrato gerado.
          <div style={S.emptyHint}>Ao ganhar um negócio recorrente, gere um contrato para acompanhar MRR e projetos.</div>
        </div>
      ) : (
        <div>
          {contracts.map((c) => (
            <div key={c.id} style={S.contractCard}>
              <div style={S.contractHead}>
                <div style={{ minWidth: 0 }}>
                  <div style={S.listTitle}>
                    {c.valor_total != null ? fmtBRL(c.valor_total) : 'Contrato'}
                    {c.mrr != null && <span style={S.primaryTag}>MRR {fmtBRL(c.mrr)}</span>}
                  </div>
                  <div style={S.listMeta}>
                    {c.data_inicio ? fmtDate(c.data_inicio) : '—'}
                    {c.data_fim ? ` → ${fmtDate(c.data_fim)}` : ''}
                  </div>
                </div>
                <div style={S.rowPills}>
                  <span style={{ ...S.pill, color: CONTRACT_STATUS_COLORS[c.status] || 'var(--sec)', borderColor: CONTRACT_STATUS_COLORS[c.status] || 'var(--soft)' }}>{c.status}</span>
                  <button style={S.itemRemove} onClick={() => remove(c.id)} title="Excluir contrato">
                    <Icon n="x" s={13} sw={2} />
                  </button>
                </div>
              </div>
              <ProjectsMini contractId={c.id} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Projetos de um contrato (entregas da Operação). Sub-bloco do ContractsTab.
function ProjectsMini({ contractId }) {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [form, setForm] = useState({ nome: '', status: 'aguardando_pagamento', prazo: '' })

  const reload = async () => setProjects(await getProjects(contractId))

  useEffect(() => {
    let alive = true
    getProjects(contractId)
      .then((ps) => alive && setProjects(ps))
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [contractId])

  const submit = async () => {
    if (!form.nome.trim() || saving) return
    setSaving(true); setErr(null)
    try {
      await createProject({
        contract_id: contractId, nome: form.nome.trim(),
        status: form.status, prazo: form.prazo || null,
      })
      setForm({ nome: '', status: 'aguardando_pagamento', prazo: '' })
      setAdding(false)
      await reload()
    } catch (e) { setErr(e.message) } finally { setSaving(false) }
  }

  const cycleStatus = async (p) => {
    const i = PROJECT_STATUS.indexOf(p.status)
    const next = PROJECT_STATUS[(i + 1) % PROJECT_STATUS.length]
    try { await updateProject(p.id, { status: next }); await reload() }
    catch (e) { setErr(e.message) }
  }

  const remove = async (id) => {
    try { await deleteProject(id); await reload() }
    catch (e) { setErr(e.message) }
  }

  return (
    <div style={S.projectsBlock}>
      <div style={S.projectsHead}>
        <span style={S.projectsTitle}>Projetos</span>
        <button style={S.projAddBtn} onClick={() => setAdding((v) => !v)}>
          <Icon n="plus" s={12} sw={2} /> Adicionar
        </button>
      </div>
      {err && <div style={S.error}>{err}</div>}

      {adding && (
        <div style={S.addBox}>
          <input style={S.input} placeholder="Nome do projeto" value={form.nome} autoFocus
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} />
          <div style={S.addGrid}>
            <select style={S.input} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              {PROJECT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input style={S.input} type="date" value={form.prazo}
              onChange={(e) => setForm((f) => ({ ...f, prazo: e.target.value }))} />
          </div>
          <div style={S.addFooter}>
            <button style={S.btnGhostSm} onClick={() => setAdding(false)}>Cancelar</button>
            <button style={S.btnPrimarySm} onClick={submit} disabled={saving || !form.nome.trim()}>
              {saving ? 'Salvando…' : 'Adicionar'}
            </button>
          </div>
        </div>
      )}

      {loading ? null : !projects.length ? (
        !adding && <div style={S.projectsEmpty}>Nenhum projeto.</div>
      ) : (
        projects.map((p) => (
          <div key={p.id} style={S.projectRow}>
            <div style={{ minWidth: 0 }}>
              <div style={S.projectName}>{p.nome}</div>
              {p.prazo && <div style={S.listMeta}>prazo {fmtDate(p.prazo)}</div>}
            </div>
            <div style={S.rowPills}>
              <button
                style={{ ...S.pill, cursor: 'pointer', background: 'transparent', color: PROJECT_STATUS_COLORS[p.status] || 'var(--sec)', borderColor: PROJECT_STATUS_COLORS[p.status] || 'var(--soft)' }}
                onClick={() => cycleStatus(p)} title="Avançar status"
              >
                {p.status}
              </button>
              <button style={S.itemRemove} onClick={() => remove(p.id)} title="Remover projeto">
                <Icon n="x" s={12} sw={2} />
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  )
}

// ── Aba Atendimentos ─────────────────────────────────────────────────────
// Traz a conversa do módulo Atendimento para o mesmo lead (contact_id) do
// Deal/Lead aberto — sem duplicar a inbox, só lê. Reaproveita os balões
// (`at-*`, atendimento.css) fora do grid `.at-inbox`: as custom properties de
// cor que esses seletores usam ficam definidas via `style` no wrapper abaixo.
const AT_VARS = {
  '--at-blue': '#2563eb', '--at-blue-dark': '#1d4ed8',
  '--at-line': '#eaecf0', '--at-line-strong': '#dfe3e8',
  '--at-ink': '#101828', '--at-ink-2': '#475467', '--at-ink-3': '#98a2b3',
}

export function ServiceTab({ contactId }) {
  const navigate = useNavigate()
  const [convs, setConvs] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [msgLoading, setMsgLoading] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let alive = true
    setConvs([]); setActiveId(null); setMessages([]); setErr(null)
    if (!contactId) { setLoading(false); return }
    setLoading(true)
    atListConversations({ contactId })
      .then((rows) => {
        if (!alive) return
        setConvs(rows)
        setActiveId(rows[0]?.id || null)
      })
      .catch((e) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [contactId])

  useEffect(() => {
    let alive = true
    setMessages([])
    if (!activeId) return
    setMsgLoading(true)
    atListMessages(activeId, { limit: 50 })
      .then((rows) => alive && setMessages(rows))
      .catch(() => alive && setMessages([]))
      .finally(() => alive && setMsgLoading(false))
    return () => { alive = false }
  }, [activeId])

  if (!contactId) {
    return <div style={S.empty}>Nenhum lead vinculado para trazer o atendimento.</div>
  }
  if (loading) return <div style={S.muted}>Carregando…</div>
  if (err) return <div style={S.error}>{err}</div>
  if (!convs.length) {
    return (
      <div style={S.empty}>
        Nenhum atendimento registrado.
        <div style={S.emptyHint}>As conversas deste lead no módulo Atendimento aparecerão aqui.</div>
      </div>
    )
  }

  const groups = groupByDay(messages)

  return (
    <div>
      {convs.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {convs.map((c) => {
            const ch = CHANNEL_META[c.channel]
            return (
              <button
                key={c.id}
                onClick={() => setActiveId(c.id)}
                style={{
                  ...S.choiceChip,
                  ...(c.id === activeId ? S.choiceChipActive : null),
                  display: 'inline-flex', alignItems: 'center', gap: 5, textTransform: 'none',
                }}
              >
                {ch && <Icon n={ch.icon} s={12} />} {ch?.label || c.channel} · {relTime(c.last_message_at)}
              </button>
            )
          })}
        </div>
      )}

      <div style={{ ...AT_VARS, border: '1px solid var(--soft)', borderRadius: 12, overflow: 'hidden' }}>
        <div className="at-messages" style={{ maxHeight: 420, flex: 'none' }}>
          {msgLoading ? (
            <div style={S.muted}>Carregando mensagens…</div>
          ) : !messages.length ? (
            <div style={S.empty}>Nenhuma mensagem nesta conversa ainda.</div>
          ) : (
            groups.map((g) => (
              <div key={g.key}>
                <div className="at-date-sep"><span>{g.sep}</span></div>
                {g.items.map((m) => {
                  if (m.sender_type === 'sistema') {
                    return <div key={m.id} className="at-sys-msg">{m.body}</div>
                  }
                  const out = m.sender_type === 'operador'
                  return (
                    <div key={m.id} className={`at-msg-row${out ? ' out' : ''}`}>
                      <div className={`at-bubble${out ? ' out' : ''}${m.status === 'falhou' ? ' failed' : ''}`}>
                        {m.body && <span className="at-bubble-body">{m.body}</span>}
                        {!!(m.attachments || []).length && (
                          <span className="at-bubble-body" style={{ opacity: .8 }}>
                            <Icon n="clip" s={12} /> {m.attachments.length > 1 ? `${m.attachments.length} anexos` : '1 anexo'}
                          </span>
                        )}
                        <span className="at-bubble-meta">{msgTime(m.created_at)}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button
          className="fc-ctrl primary"
          onClick={() => navigate(`/atendimento?conversation=${activeId}`)}
        >
          <Icon n="whatsapp" s={13} /> Ir para o atendimento
        </button>
      </div>
    </div>
  )
}

export const S = {
  drawer: {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1000,
    width: 'clamp(720px, 68vw, 1100px)', maxWidth: '100%',
    background: 'var(--surface)', borderLeft: '1px solid var(--soft)',
    boxShadow: '-12px 0 40px rgba(0,0,0,.18)',
    display: 'flex', flexDirection: 'column',
    transition: 'transform .26s cubic-bezier(.4,0,.2,1)',
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px', borderBottom: '1px solid var(--soft)', flexShrink: 0,
  },
  topTitle: { fontSize: 13, fontWeight: 700, color: 'var(--sec)', textTransform: 'uppercase', letterSpacing: '.04em' },
  iconBtn: { border: 'none', background: 'transparent', color: 'var(--sec)', cursor: 'pointer', padding: 4, borderRadius: 6 },

  columns: { flex: 1, display: 'flex', minHeight: 0, animation: 'leadPanelFade .22s ease' },
  leftCol: {
    width: 360, flexShrink: 0, overflowY: 'auto', padding: '18px 20px 28px',
    borderRight: '1px solid var(--soft)',
  },
  rightCol: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },

  // cabeçalho (esquerda)
  identity: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 },
  avatar: {
    width: 46, height: 46, borderRadius: '50%', flexShrink: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center', background: 'var(--text)',
    color: 'var(--surface)', fontSize: 19, fontWeight: 700,
  },
  name: { fontSize: 18, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  company: { fontSize: 13, color: 'var(--sec)', marginTop: 2 },
  metaRow: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  statusBadge: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
    border: '1px solid', borderRadius: 100, padding: '2px 10px',
  },
  metaItem: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--sec)', fontWeight: 600 },
  actions: { display: 'flex', gap: 8, marginTop: 14 },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
    border: '1px solid #2563eb', background: '#2563eb', color: '#fff',
    fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--text)',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnDanger: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--amber, #c0392b)',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', marginLeft: 'auto',
  },
  // Botão "Apagar" no rodapé do bloco de Notas/Descrição (à esquerda do "Salvar").
  btnDeleteFooter: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'transparent', color: '#c0392b',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },

  section: { marginTop: 20 },
  sectionTitle: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)', marginBottom: 10 },
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 14px' },
  field: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--ter)', marginBottom: 3 },
  fieldValue: { fontSize: 14, color: 'var(--text)', wordBreak: 'break-word' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  chip: {
    fontSize: 11, fontWeight: 600, color: 'var(--sec)', background: 'var(--bg)',
    border: '1px solid var(--soft)', borderRadius: 100, padding: '2px 9px',
  },

  // métricas
  metricsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  metricCard: { border: '1px solid var(--soft)', borderRadius: 10, padding: '10px 12px', background: 'var(--bg)' },
  metricHead: { display: 'flex', alignItems: 'center', gap: 5, color: 'var(--sec)' },
  metricLabel: { fontSize: 11, fontWeight: 700, color: 'var(--sec)' },
  metricValue: { fontSize: 17, fontWeight: 700, color: 'var(--text)', marginTop: 4 },

  // notas
  notesHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  savedTag: { fontSize: 11, fontWeight: 700, color: 'var(--green, #30d158)' },
  notesArea: {
    width: '100%', boxSizing: 'border-box', minHeight: 110, resize: 'vertical', padding: '10px 12px',
    borderRadius: 8, border: '1px solid var(--soft)', background: 'var(--bg)', color: 'var(--text)',
    fontSize: 14, lineHeight: 1.45, fontFamily: 'inherit',
  },
  notesFooter: { display: 'flex', justifyContent: 'flex-end', marginTop: 8 },

  // abas (direita)
  tabs: {
    display: 'flex', gap: 2, borderBottom: '1px solid var(--soft)', padding: '0 16px',
    flexShrink: 0, overflowX: 'auto',
  },
  tab: {
    display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent',
    padding: '14px 12px', fontSize: 13, fontWeight: 600, color: 'var(--sec)', cursor: 'pointer',
    borderBottom: '2px solid transparent', whiteSpace: 'nowrap',
  },
  tabActive: { color: 'var(--text)', borderBottomColor: 'var(--text)' },
  tabCount: {
    fontSize: 10, fontWeight: 700, color: 'var(--sec)', background: 'var(--bg)',
    border: '1px solid var(--soft)', borderRadius: 100, padding: '0 6px', minWidth: 16, textAlign: 'center',
  },
  tabBody: { flex: 1, overflowY: 'auto', padding: '16px 20px 28px' },
  tabActions: { display: 'flex', justifyContent: 'flex-end', marginBottom: 10 },

  // timeline
  timeline: { display: 'flex', flexDirection: 'column' },
  tlItem: { display: 'flex', gap: 14 },
  // coluna do ícone: círculo tintado + linha vertical (rail) descendo até o próximo
  tlRail: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
  tlIcon: {
    width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center', border: '1px solid var(--soft)',
  },
  tlLine: { flex: 1, width: 2, minHeight: 14, background: 'var(--soft)', margin: '3px 0' },
  tlBody: { minWidth: 0, flex: 1, paddingBottom: 20 },
  tlMeta: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
    fontSize: 12, color: 'var(--ter)', marginBottom: 6,
  },
  tlActor: { display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600, color: 'var(--sec)' },
  // card da descrição — fundo levemente destacado + acento na cor do tipo
  tlCard: {
    background: 'var(--bg)', border: '1px solid var(--soft)', borderRadius: 12,
    padding: '11px 14px',
  },
  tlTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text)', lineHeight: 1.3 },
  tlDesc: { fontSize: 13.5, color: 'var(--sec)', marginTop: 4, wordBreak: 'break-word', lineHeight: 1.45 },

  // listas
  listRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    padding: '10px 0', borderBottom: '1px solid var(--soft)',
  },
  listTitle: { fontSize: 14, fontWeight: 600, color: 'var(--text)' },
  doneTitle: { textDecoration: 'line-through', color: 'var(--sec)' },
  listMeta: { fontSize: 12, color: 'var(--sec)', marginTop: 2 },
  rowPills: { display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' },
  actBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--sec)',
    cursor: 'pointer', borderRadius: 6, padding: 3,
  },
  pill: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', border: '1px solid',
    borderRadius: 100, padding: '2px 9px', flexShrink: 0,
  },
  primaryTag: {
    marginLeft: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ter)',
    background: 'var(--bg)', border: '1px solid var(--soft)', borderRadius: 100, padding: '1px 7px',
  },

  // itens do negócio (deal_items)
  miniLabel: { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--sec)', marginBottom: 3 },
  itemTotal: { fontSize: 14, fontWeight: 700, color: 'var(--text)' },
  itemRemove: {
    border: 'none', background: 'transparent', color: 'var(--amber, #c0392b)',
    cursor: 'pointer', padding: 2, borderRadius: 6, display: 'flex', alignItems: 'center',
  },
  // remover negócio (card da aba Negócios): lixeira centralizada verticalmente
  dealRemove: {
    border: 'none', background: 'transparent', color: 'var(--amber, #c0392b)',
    cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center',
    justifyContent: 'center', alignSelf: 'center', flexShrink: 0,
  },
  itemsTotalRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 0 2px', fontSize: 13, fontWeight: 700, color: 'var(--sec)',
  },
  itemsTotalValue: { fontSize: 16, fontWeight: 700, color: 'var(--text)' },

  // contratos & projetos (F3)
  contractCard: {
    border: '1px solid var(--soft)', borderRadius: 10, padding: 12,
    marginBottom: 12, background: 'var(--bg)',
  },
  contractHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  projectsBlock: { marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--soft)' },
  projectsHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  projectsTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)' },
  projAddBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent',
    color: 'var(--sec)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0,
  },
  projectsEmpty: { fontSize: 12, color: 'var(--ter)', padding: '4px 0' },
  projectRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    padding: '7px 0', borderBottom: '1px solid var(--soft)',
  },
  projectName: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  contractLocked: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    borderRadius: 8, border: '1px dashed var(--soft)', background: 'var(--bg)',
    color: 'var(--sec)', fontSize: 13, lineHeight: 1.4,
  },

  // segmented (modo de adicionar item: novo vs catálogo)
  segRow: { display: 'flex', gap: 4, background: 'var(--surface)', border: '1px solid var(--soft)', borderRadius: 8, padding: 3 },
  segBtn: {
    flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', background: 'transparent',
    color: 'var(--sec)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  segBtnActive: { background: 'var(--text)', color: 'var(--surface)' },

  // cards de negócios (aba Negócios — padrão Datacrazy)
  dealCards: { display: 'flex', flexDirection: 'column', gap: 10 },
  dealCard: {
    position: 'relative', border: '1px solid var(--soft)', borderRadius: 12,
    padding: '12px 14px', background: 'var(--bg)',
    display: 'flex', alignItems: 'center', gap: 12,
  },
  dealCardClickable: { cursor: 'pointer', paddingRight: 34 },
  dealCardMain: { flex: 1, minWidth: 0 },
  dealCardSide: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  dealCardName: { fontSize: 14.5, fontWeight: 700, color: 'var(--text)', minWidth: 0 },
  dealCardMeta: { display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8 },
  dealCardMetaItem: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--sec)', fontWeight: 500 },
  dealCardChevron: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    color: 'var(--ter)', display: 'flex',
  },

  // modal de adicionar item (sobrepõe o painel/modal do negócio)
  mOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1200,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px',
  },
  mBox: {
    width: 440, maxWidth: '100%', background: 'var(--surface)', border: '1px solid var(--soft)',
    borderRadius: 14, padding: 18, boxShadow: '0 16px 48px rgba(0,0,0,.3)',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  mHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  mTitle: { margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)', display: 'inline-flex', alignItems: 'center', gap: 7 },

  // modal "Adicionar item" (redesenho)
  itemBox: {
    width: 480, maxWidth: '100%', background: 'var(--surface)', border: '1px solid var(--soft)',
    borderRadius: 16, padding: 20, boxShadow: '0 16px 48px rgba(0,0,0,.3)',
    display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '86vh',
  },
  itemBody: { display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' },
  optional: { fontWeight: 500, color: 'var(--ter)', textTransform: 'none' },
  itemPreview: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '11px 14px', borderRadius: 10, border: '1px solid var(--soft)', background: 'var(--bg)',
  },
  itemPreviewLabel: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ter)' },
  itemPreviewValue: { fontSize: 18, fontWeight: 700, color: 'var(--text)' },
  itemFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: 8,
    paddingTop: 14, borderTop: '1px solid var(--soft)',
  },
  mX: { border: 'none', background: 'transparent', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: 'var(--sec)' },

  // form nova atividade
  addBox: { border: '1px solid var(--soft)', borderRadius: 10, padding: 12, marginBottom: 12, background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 8 },
  actForm: {
    border: '1px solid var(--soft)', borderRadius: 12, padding: 16, marginBottom: 14,
    background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 2px 10px rgba(0,0,0,.04)',
  },
  actFormHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  actFormTitle: {
    display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700,
    color: 'var(--text)',
  },
  actFormClose: {
    border: 'none', background: 'transparent', color: 'var(--sec)', cursor: 'pointer',
    padding: 2, borderRadius: 6, display: 'flex', alignItems: 'center',
  },
  actField: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  choiceChip: {
    padding: '5px 12px', borderRadius: 100, border: '1px solid var(--soft)',
    background: 'var(--surface)', color: 'var(--sec)', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', textTransform: 'capitalize',
  },
  choiceChipActive: { background: 'var(--text)', color: 'var(--surface)', borderColor: 'var(--text)' },
  actFormFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2,
    paddingTop: 12, borderTop: '1px solid var(--soft)',
  },
  addGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  addFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13,
  },
  btnPrimarySm: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 8,
    border: '1px solid #2563eb', background: '#2563eb', color: '#fff',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  btnGhostSm: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 8,
    border: '1px solid var(--soft)', background: 'var(--surface)', color: 'var(--text)',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  btnDangerSm: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 11px', borderRadius: 8,
    border: '1px solid #c0392b', background: '#c0392b', color: '#fff',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
  confirmMsg: { fontSize: 13, lineHeight: 1.5, color: 'var(--sec)', margin: '2px 0 6px' },
  muted: { color: 'var(--sec)', fontSize: 13, padding: '20px' },
  empty: { color: 'var(--sec)', fontSize: 13, padding: '28px 8px', textAlign: 'center' },
  emptyHint: { fontSize: 12, color: 'var(--ter)', marginTop: 6 },
  error: {
    padding: '8px 12px', borderRadius: 8, background: 'var(--amber-bg, #fdecea)',
    border: '1px solid var(--amber-border, #f5c6cb)', color: 'var(--amber, #c0392b)', fontSize: 13, marginTop: 8,
  },
}
